/**
 * Utilities for converting between SSML break emoji (⏱) and ElevenLabs v3 audio tags
 *
 * ElevenLabs v3 models don't support SSML tags, so we need to convert:
 * - v2/SSML: ⏱ represents a 0.5s break
 * - v3: [short pause] for pauses
 */

/**
 * Check if a model is an ElevenLabs v3 model
 */
export function isV3Model(modelId?: string): boolean {
    if (!modelId) {
        return false;
    }
    return modelId.includes('eleven_v3');
}

/**
 * Convert ⏱ emoji to v3 audio tag [short pause]
 * Used when switching from v2 to v3 model
 */
export function convertTimerEmojiToV3Tags(text: string): string {
    return text.replace(/⏱/g, '[short pause]');
}

/**
 * Convert v3 audio tags back to ⏱ emoji
 * Used when switching from v3 to v2 model
 */
export function convertV3TagsToTimerEmoji(text: string): string {
    // Convert both short and long pause to the standard timer emoji
    return text.replace(/\[short pause\]/g, '⏱').replace(/\[long pause\]/g, '⏱');
}

/**
 * Get the appropriate break representation for insertion based on model
 * @param modelId The current ElevenLabs model ID
 * @returns The break string to insert (either ⏱ or [short pause])
 */
export function getBreakForModel(modelId?: string): string {
    return isV3Model(modelId) ? '[short pause]' : '⏱';
}

/**
 * Convert text between v2 and v3 break formats
 * @param text The text to convert
 * @param targetModelId The model ID we're converting TO
 * @returns Converted text
 */
export function convertTextForModel(text: string, targetModelId?: string): string {
    if (isV3Model(targetModelId)) {
        // Converting to v3: ⏱ → [short pause]
        return convertTimerEmojiToV3Tags(text);
    } else {
        // Converting to v2: [short pause]/[long pause] → ⏱
        return convertV3TagsToTimerEmoji(text);
    }
}
